import { useState, useEffect } from "react";
import NewTaskDialog from "@/components/NewTaskDialog";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { purgeCachesOnLogout } from "@/lib/cachePurge";
import { getSensitivePagesToken } from "@/lib/sensitivePages";
import { isTeamHubHost, teamHomePath } from "@/lib/teamHub";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Package, 
  BarChart3, 
  ClipboardList, 
  Cake, 
  Users, 
  Settings, 
  Menu,
  X,
  Megaphone,
  FileText,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Users2,
  Bot,
  MessageCircle,
  Plus,
  CheckSquare,
  Image,
  CalendarDays,
  MoreHorizontal,
  Activity,
} from "lucide-react";
import { prefetchOrdersToday, prefetchOrderCatalog } from "@/features/orders/ordersQueryClient";
import { prefetchInventory } from "@/features/inventory/inventoryQueryClient";
import { prefetchCustomers } from "@/features/customers/customersQueryClient";
import { prefetchDashboard } from "@/features/dashboard/dashboardQueryClient";
import { prefetchTasks } from "@/features/tasks/tasksQueryClient";
import { prefetchSettings } from "@/features/settings/settingsQueryClient";
import { prefetchRecipes } from "@/features/recipes/recipesQueryClient";
import { prefetchCatalog } from "@/features/catalog/catalogQueryClient";
import { prefetchMarketing } from "@/features/marketing/marketingQueryClient";
import { prefetchTeam } from "@/features/team/teamQueryClient";
import { prefetchAnalytics } from "@/features/analytics/analyticsQueryClient";
import { prefetchAppAnalytics } from "@/features/app-analytics/appAnalyticsQueryClient";
import { prefetchDailyReport } from "@/features/daily-report/dailyReportQueryClient";
import { prefetchWeAgentReport } from "@/features/we-agent-report/weAgentReportQueryClient";
import { prefetchCalendar } from "@/features/calendar/calendarQueryClient";
import { prefetchAiQuickActions } from "@/features/ai-assistant/aiAssistantQueryClient";
import StaffNavRail from "@/components/mobile/StaffNavRail";

function prefetchOrdersChunk() {
  void import("@/features/orders/OrdersV2Root");
  void import("@/features/orders/OrderDetail.v2");
  void import("@/features/orders/NewOrderV2Root");
}

function prefetchDashboardChunk() {
  void import("@/features/dashboard/DashboardV2Root");
}

function prefetchAnalyticsChunk() {
  void import("@/features/analytics/AnalyticsV2Root");
}

function prefetchAppAnalyticsChunk() {
  void import("@/features/app-analytics/AppAnalyticsV2Root");
}

function prefetchDailyReportChunk() {
  void import("@/features/daily-report/DailyReportV2Root");
}

function prefetchWeAgentReportChunk() {
  void import("@/features/we-agent-report/WeAgentReportV2Root");
}

function prefetchMarketingChunk() {
  void import("@/features/marketing/MarketingV2Root");
}

function prefetchInventoryChunk() {
  void import("@/features/inventory/InventoryV2Root");
}

function prefetchRecipesChunk() {
  void import("@/features/recipes/BakingV2Root");
}

function prefetchCatalogChunk() {
  void import("@/features/catalog/CatalogV2Root");
}

function prefetchCustomersChunk() {
  void import("@/features/customers/CustomersV2Root");
}

function prefetchTeamChunk() {
  void import("@/features/team/TeamV2Root");
}

function prefetchTasksChunk() {
  void import("@/features/tasks/TasksV2Root");
}

function prefetchSettingsChunk() {
  void import("@/features/settings/SettingsV2Root");
}

function prefetchCalendarChunk() {
  void import("@/features/calendar/CalendarV2Root");
}

function prefetchAiAssistantChunk() {
  void import("@/features/ai-assistant/AiAssistantV2Root");
}

function prefetchChatChunk() {
  void import("@/features/chat/ChatV2Root");
}

function getAnalyticsDefaultRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
  const end = today.toISOString().split("T")[0];
  return { start, end };
}

function getTodayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

function navWarmHandlers(currentPath, routePath, warmFn) {
  const onRoute =
    routePath === "/"
      ? currentPath === "/"
      : currentPath === routePath || currentPath.startsWith(`${routePath}/`);
  if (onRoute) return {};
  return { onMouseEnter: warmFn, onFocus: warmFn };
}

function warmOrders() {
  prefetchOrdersChunk();
  void prefetchOrdersToday();
  void prefetchOrderCatalog();
}

function warmNewOrder() {
  prefetchOrdersChunk();
  void prefetchOrderCatalog();
}

function warmInventory() {
  prefetchInventoryChunk();
  void prefetchInventory();
}

function warmCustomers() {
  prefetchCustomersChunk();
  void prefetchCustomers();
}

function warmAllNavChunks() {
  prefetchDashboardChunk();
  prefetchAnalyticsChunk();
  prefetchAppAnalyticsChunk();
  prefetchDailyReportChunk();
  prefetchMarketingChunk();
  prefetchRecipesChunk();
  prefetchCatalogChunk();
  prefetchTeamChunk();
  prefetchTasksChunk();
  prefetchSettingsChunk();
  prefetchCalendarChunk();
  prefetchAiAssistantChunk();
  prefetchChatChunk();
}

function warmRecipes() {
  prefetchRecipesChunk();
  void prefetchRecipes();
}

function warmMarketing() {
  prefetchMarketingChunk();
  void prefetchMarketing();
}

function warmTeam() {
  prefetchTeamChunk();
  void prefetchTeam();
}

function warmDashboard() {
  prefetchDashboardChunk();
  void prefetchDashboard();
}

function warmAnalytics() {
  prefetchAnalyticsChunk();
  if (!getSensitivePagesToken()) return;
  const { start, end } = getAnalyticsDefaultRange();
  void prefetchAnalytics({ start, end });
}

function warmAppAnalytics() {
  prefetchAppAnalyticsChunk();
  void prefetchAppAnalytics("7d");
}

function warmDailyReport() {
  prefetchDailyReportChunk();
  if (!getSensitivePagesToken()) return;
  void prefetchDailyReport(getTodayIsoDate());
}

function warmWeAgentReport() {
  prefetchWeAgentReportChunk();
  void prefetchWeAgentReport(getTodayIsoDate());
}

function warmCatalog() {
  prefetchCatalogChunk();
  void prefetchCatalog();
}

function warmTasks() {
  prefetchTasksChunk();
  void prefetchTasks();
}

function warmSettings() {
  prefetchSettingsChunk();
  void prefetchSettings();
}

function warmCalendar() {
  prefetchCalendarChunk();
  void prefetchCalendar();
}

function warmAiAssistant() {
  prefetchAiAssistantChunk();
  void prefetchAiQuickActions();
}

function warmChat() {
  prefetchChatChunk();
}

function warmAllNavData() {
  void prefetchDashboard();
  void prefetchOrdersToday();
  void prefetchOrderCatalog();
  void prefetchInventory();
  void prefetchCustomers();
  void prefetchTasks();
  void prefetchSettings();
  void prefetchRecipes();
  void prefetchCatalog();
  void prefetchMarketing();
  void prefetchTeam();
  void prefetchCalendar();
  void prefetchAiQuickActions();
  void prefetchAppAnalytics("7d");
  if (getSensitivePagesToken()) {
    const { start, end } = getAnalyticsDefaultRange();
    void prefetchAnalytics({ start, end });
    void prefetchDailyReport(getTodayIsoDate());
  }
}

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/orders", icon: Package, label: "Orders" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/app-analytics", icon: Activity, label: "App Analytics" },
  { path: "/daily-report", icon: FileText, label: "Daily Report" },
  { path: "/we-agent-report", icon: Bot, label: "WE Agent" },
  { path: "/marketing", icon: Megaphone, label: "Marketing" },
  { path: "/inventory", icon: ClipboardList, label: "Inventory" },
  { path: "/baking", icon: Cake, label: "Recipes" },
  { path: "/catalog", icon: Image, label: "Catalog" },
  { path: "/customers", icon: Users, label: "Customers" },
  { path: "/team", icon: Users2, label: "Team" },
  { path: "/tasks", icon: CheckSquare, label: "Tasks" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const mobileTabItems = [
  { path: "/", icon: LayoutDashboard, label: "Home", match: (p) => p === "/" },
  { path: "/orders", icon: Package, label: "Orders", match: (p) => p === "/orders" || p.startsWith("/orders/") },
  { path: "/tasks", icon: CheckSquare, label: "Tasks", match: (p) => p === "/tasks" || p.startsWith("/tasks/") },
  { path: "/calendar", icon: CalendarDays, label: "Calendar", match: (p) => p === "/calendar" || p.startsWith("/calendar/") },
];

function isTabActive(path, matchFn) {
  const normalized = path.replace(/\/$/, "") || "/";
  return matchFn(normalized);
}

/** Says what the button does: leaves Ops, not the staff session behind it. */
const EXIT_OPS_LABEL = "Exit Ops";

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "") || "/";
  const showFloatingNewOrder =
    path !== "/orders" && !path.startsWith("/customers/leads");

  const closeSidebar = () => setSidebarOpen(false);
  const openSidebar = () => setSidebarOpen(true);

  /**
   * Drop the Ops elevation, not the staff session.
   *
   * The manager token is a 12-hour elevation on top of a 30-day TS session —
   * closer to sudo than to a second account. Since Ops moved onto the Team
   * origin both live in the same localStorage, and the old `navigate("/login")`
   * became a loop: ManageLogin mounts TsSessionGate, which sees the still-valid
   * TS session and immediately trades it for a fresh manager token. You appeared
   * to stay signed in because you were being re-elevated on the way out.
   *
   * So leave to the hub instead. Ending the staff session as well would sign the
   * person out of Kitchen and Chat on that device, which this button never
   * promised; the hub's own Logout is the full sign-out.
   */
  const handleExitOps = async () => {
    logout();
    await purgeCachesOnLogout();
    if (isTeamHubHost()) {
      window.location.href = teamHomePath();
      return;
    }
    navigate("/login", { replace: true });
  };

  const mobileTabActive = mobileTabItems.some((item) => isTabActive(path, item.match));

  useEffect(() => {
    // Chunks: download immediately so first nav click never hits Suspense spinner.
    prefetchOrdersChunk();
    prefetchInventoryChunk();
    prefetchCustomersChunk();
    warmAllNavChunks();

    // All nav data: start immediately (persisted cache restores on reload via mv2CachePersist).
    warmAllNavData();
  }, []);

  const dashboardNavHandlers = navWarmHandlers(path, "/", warmDashboard);
  const analyticsNavHandlers = navWarmHandlers(path, "/analytics", warmAnalytics);
  const appAnalyticsNavHandlers = navWarmHandlers(path, "/app-analytics", warmAppAnalytics);
  const dailyReportNavHandlers = navWarmHandlers(path, "/daily-report", warmDailyReport);
  const weAgentReportNavHandlers = navWarmHandlers(path, "/we-agent-report", warmWeAgentReport);
  const catalogNavHandlers = navWarmHandlers(path, "/catalog", warmCatalog);
  const tasksNavHandlers = navWarmHandlers(path, "/tasks", warmTasks);
  const settingsNavHandlers = navWarmHandlers(path, "/settings", warmSettings);
  const chatNavHandlers = navWarmHandlers(path, "/tc", warmChat);
  const aiAssistantNavHandlers = navWarmHandlers(path, "/ai-assistant", warmAiAssistant);
  const calendarNavHandlers = navWarmHandlers(path, "/calendar", warmCalendar);
  const ordersNavHandlers = navWarmHandlers(path, "/orders", warmOrders);
  const inventoryNavHandlers = navWarmHandlers(path, "/inventory", warmInventory);
  const customersNavHandlers = navWarmHandlers(path, "/customers", warmCustomers);
  const recipesNavHandlers = navWarmHandlers(path, "/baking", warmRecipes);
  const marketingNavHandlers = navWarmHandlers(path, "/marketing", warmMarketing);
  const teamNavHandlers = navWarmHandlers(path, "/team", warmTeam);

  return (
    <div className="flex min-h-[100dvh] min-h-screen">
      {/* The staff rail spans the top above all of Ops' own chrome, which is
          entirely fixed. It publishes --staff-rail-h; every fixed element below
          offsets against that rather than a literal, because the rail is not
          always present — Layout also serves the Manage host, where it declines
          to draw and the variable falls back to the safe-area inset alone.
          `current` is passed because Ops re-bases the router at /ops, leaving
          useLocation() reporting "/" for the dashboard. */}
      <StaffNavRail current="manage" pinned />

      {/* Mobile Header */}
      <header className="mobile-app-header lg:hidden fixed top-[var(--staff-rail-h)] left-0 right-0 z-50 flex h-14 items-center justify-between bg-[#EE2677] px-4 shadow-md">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-sm font-heading font-bold text-white">
            TTT
          </div>
          <div className="min-w-0">
            <h1 className="truncate text-base font-heading font-bold leading-tight text-white">
              The Table Top
            </h1>
            <p className="truncate text-[10px] text-white/80">Bakery Management</p>
          </div>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="flex h-11 w-11 items-center justify-center rounded-xl text-white transition-colors active:bg-white/20"
          data-testid="mobile-menu-btn"
          aria-label={sidebarOpen ? "Close menu" : "Open menu"}
        >
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - Collapsible on Desktop */}
      <aside 
        className={`
          fixed left-0 top-[var(--staff-rail-h)] h-[calc(100%-var(--staff-rail-h))] bg-[#EE2677] text-white flex flex-col shadow-xl z-50
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'lg:w-[70px]' : 'lg:w-[220px]'}
          w-[280px]
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`p-4 flex items-center ${sidebarCollapsed ? 'lg:justify-center' : 'justify-between'}`}>
          <div className={`${sidebarCollapsed ? 'lg:hidden' : ''}`}>
            <h1 className="text-2xl font-heading font-bold text-white tracking-tight">
              The Table Top
            </h1>
            <p className="text-xs mt-0.5 text-white/85">Bakery Management</p>
          </div>
          {/* Collapsed Logo - Icon only */}
          <div className={`hidden ${sidebarCollapsed ? 'lg:flex' : 'lg:hidden'} items-center justify-center`}>
            <span className="text-2xl font-heading font-bold text-white">TTT</span>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={closeSidebar}
              {...(item.path === "/" ? dashboardNavHandlers : {})}
              {...(item.path === "/orders" ? ordersNavHandlers : {})}
              {...(item.path === "/analytics" ? analyticsNavHandlers : {})}
              {...(item.path === "/app-analytics" ? appAnalyticsNavHandlers : {})}
              {...(item.path === "/daily-report" ? dailyReportNavHandlers : {})}
              {...(item.path === "/we-agent-report" ? weAgentReportNavHandlers : {})}
              {...(item.path === "/marketing" ? marketingNavHandlers : {})}
              {...(item.path === "/inventory" ? inventoryNavHandlers : {})}
              {...(item.path === "/baking" ? recipesNavHandlers : {})}
              {...(item.path === "/catalog" ? catalogNavHandlers : {})}
              {...(item.path === "/customers" ? customersNavHandlers : {})}
              {...(item.path === "/team" ? teamNavHandlers : {})}
              {...(item.path === "/tasks" ? tasksNavHandlers : {})}
              {...(item.path === "/settings" ? settingsNavHandlers : {})}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200
                ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
                ${isActive 
                  ? "bg-white text-[#EE2677] shadow-sm font-medium" 
                  : "hover:bg-white/20 text-white"
                }`
              }
              data-testid={`nav-${(item.label || '').toLowerCase().replace(' ', '-')}`}
              title={sidebarCollapsed ? item.label : ''}
            >
              <item.icon size={20} className="flex-shrink-0" />
              <span className={`${sidebarCollapsed ? 'lg:hidden' : ''} text-sm`}>{item.label}</span>
            </NavLink>
          ))}

          <NavLink
            to="/tc"
            onClick={closeSidebar}
            {...chatNavHandlers}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200
              ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
              ${isActive
                ? "bg-white text-[#EE2677] shadow-sm font-medium"
                : "hover:bg-white/20 text-white"
              }`
            }
            title={sidebarCollapsed ? "Table Top Chat" : ""}
          >
            <MessageCircle size={20} className="flex-shrink-0" />
            <span className={`${sidebarCollapsed ? 'lg:hidden' : ''} text-sm`}>Table Top Chat</span>
          </NavLink>

          {/* AI Assistant - Links to full page */}
          <NavLink
            to="/ai-assistant"
            onClick={closeSidebar}
            {...aiAssistantNavHandlers}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200
              ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}
              ${isActive
                ? "bg-white text-[#EE2677] shadow-sm font-medium"
                : "hover:bg-white/20 text-white"
              }`
            }
          >
            <Bot size={20} className="flex-shrink-0" />
            <span className={`${sidebarCollapsed ? 'lg:hidden' : ''} text-sm`}>AI Assistant</span>
          </NavLink>
        </nav>

        {/* Exit Ops — drops the manager elevation, keeps the staff session. */}
        <div className={`px-2 pb-1 ${sidebarCollapsed ? 'lg:px-2' : ''}`}>
          <button
            onClick={handleExitOps}
            className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg transition-all duration-200 hover:bg-white/20 text-white hover:text-white ${sidebarCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
            title={sidebarCollapsed ? EXIT_OPS_LABEL : ""}
          >
            <LogOut size={20} className="flex-shrink-0" />
            <span className={`${sidebarCollapsed ? 'lg:hidden' : ''} text-sm`}>{EXIT_OPS_LABEL}</span>
          </button>
        </div>

        {/* Collapse Toggle Button - Desktop Only */}
        <div className="hidden lg:flex justify-center p-3 border-t border-white/20">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors text-white"
            data-testid="sidebar-collapse-btn"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {sidebarCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        {/* Footer */}
        <div className={`p-3 border-t border-white/25 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
          <p className="text-xs text-white/80 text-center">
            © 2026 The Table Top
          </p>
        </div>
      </aside>

      {/* Global floating actions — top center of content area (fixed, no layout shift) */}
      <div
        className={`pointer-events-none fixed z-[60] flex justify-center px-4
          top-[calc(4rem+var(--staff-rail-h))] left-0 right-0
          lg:top-[calc(1.5rem+var(--staff-rail-h))] lg:right-0 ${sidebarCollapsed ? "lg:left-[70px]" : "lg:left-[220px]"}`}
        data-testid="global-action-buttons"
      >
        <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2">
          {showFloatingNewOrder && (
            <Button
              type="button"
              onClick={() => navigate("/orders/new")}
              onMouseEnter={warmNewOrder}
              onFocus={warmNewOrder}
              className="h-11 min-h-11 rounded-xl border-2 border-[#EE2677] bg-[#EE2677] px-4 text-sm font-bold text-white shadow-lg hover:bg-[#d91f69] sm:h-12 sm:px-5 sm:text-base"
              data-testid="floating-new-order-btn"
            >
              <Plus size={20} className="mr-2 shrink-0" />
              New order
            </Button>
          )}
          <Button
            type="button"
            onClick={() => setNewTaskOpen(true)}
            className="h-11 min-h-11 rounded-xl border-2 border-[#6B0000] bg-[#8B0000] px-4 text-sm font-bold text-white shadow-lg hover:bg-[#6B0000] sm:h-12 sm:px-5 sm:text-base"
            data-testid="floating-new-task-btn"
          >
            <CheckSquare size={20} className="mr-2 shrink-0" />
            New task
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <main 
        className={`
          mobile-app-main flex-1 min-h-[100dvh] min-h-screen bg-[#FFFBF8]
          pt-[calc(3.5rem+var(--staff-rail-h))] px-4 pb-28
          sm:px-6 lg:px-8 lg:pb-10 lg:pt-[calc(1.5rem+var(--staff-rail-h))]
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'lg:ml-[70px]' : 'lg:ml-[220px]'}
        `}
      >
        <div className="mx-auto w-full max-w-6xl lg:max-w-none">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      <nav
        className="mobile-tab-bar lg:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-[#EBB3A9]/40 bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
        aria-label="Primary navigation"
      >
        <div className="grid h-16 grid-cols-5">
          {mobileTabItems.map((item) => {
            const active = isTabActive(path, item.match);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                {...(item.path === "/orders" ? ordersNavHandlers : {})}
                className={`mobile-tab-item flex flex-col items-center justify-center gap-0.5 px-1 transition-colors active:scale-95 ${
                  active ? "text-[#EE2677]" : "text-[#2D2D2D]/55"
                }`}
                data-testid={`mobile-tab-${item.label.toLowerCase()}`}
              >
                <item.icon size={22} strokeWidth={active ? 2.5 : 2} />
                <span className={`text-[10px] leading-none ${active ? "font-bold" : "font-medium"}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
          <button
            type="button"
            onClick={openSidebar}
            className={`mobile-tab-item flex flex-col items-center justify-center gap-0.5 px-1 transition-colors active:scale-95 ${
              sidebarOpen || (!mobileTabActive && path !== "/login") ? "text-[#EE2677]" : "text-[#2D2D2D]/55"
            }`}
            data-testid="mobile-tab-more"
            aria-label="More menu"
          >
            <MoreHorizontal size={22} strokeWidth={sidebarOpen ? 2.5 : 2} />
            <span className={`text-[10px] leading-none ${sidebarOpen ? "font-bold" : "font-medium"}`}>
              More
            </span>
          </button>
        </div>
      </nav>

      <NewTaskDialog open={newTaskOpen} onOpenChange={setNewTaskOpen} />
    </div>
  );
}
